import { Ref } from '@solid-primitives/refs';

import { Style, AnimateOptions } from './animate';
import { transition, Options } from './transition';
import { fix1px, grow, linear } from '../../libs/animateConfig';
import { microTasks } from './tools';
import { Flip } from './Flip';
import { useRouterStacks } from '../router/Context';

/**
 * 完全基于dom的animate来动画，本身并不保存最终状态，让动画的移除和添加变得容易
 * @param props
 * @returns
 */
function Transition(props: ParentProps<Options>) {
  const [pps, others] = splitProps(props, ['children']);
  return (
    <Ref
      ref={(el: any) => {
        if (!el) return;
        transition(el, others);
      }}
    >
      {pps.children}
    </Ref>
  );
}

function makeOptions(props: NoKeyframeOptions, css: (t: number) => Style): AnimateOptions {
  return {
    duration: props.duration,
    delay: props.delay,
    css: (t: number) => {
      // 确保 t 是有效的数值，且在 0-1 范围内
      const validT = isNaN(t) || t < 0 ? 0 : t > 1 ? 1 : t;
      return css(validT);
    },
  };
}

function Fade(props: NoKeyframeOptions) {
  return (
    <Transition
      {...props}
      config={(el) => {
        const style = getComputedStyle(el);
        const rawOpacity = Number(style.opacity);
        const targetOpacity = isNaN(rawOpacity) ? 1 : rawOpacity;
        return makeOptions(props, (t) => ({ opacity: `${t * targetOpacity}` }));
      }}
    />
  );
}

function Scale(
  props: NoKeyframeOptions<{
    start?: number;
  }>
) {
  return (
    <Transition
      {...props}
      config={(el) => {
        const style = getComputedStyle(el);
        const rawOpacity = Number(style.opacity);
        const targetOpacity = isNaN(rawOpacity) ? 1 : rawOpacity;
        const transform = style.transform === 'none' ? '' : style.transform;
        const start = props.start || 0;
        const total = 1 - start;
        return makeOptions(props, (t) => ({
          transform: `${transform} scale(${start + t * total})`,
          opacity: `${targetOpacity * t}`,
        }));
      }}
    />
  );
}

function Fly(
  props: NoKeyframeOptions<{
    x?: number;
    y?: number;
  }>
) {
  return (
    <Transition
      {...props}
      config={(el) => {
        const style = getComputedStyle(el);
        const rawOpacity = Number(style.opacity);
        const targetOpacity = isNaN(rawOpacity) ? 1 : rawOpacity;
        const transform = style.transform === 'none' ? '' : style.transform;
        const x = props.x || 0;
        const y = props.y || 0;
        return makeOptions(props, (t) => {
          const t2 = 1 - t;
          return {
            transform: `${transform} translate(${t2 * x}px, ${t2 * y}px)`,
            opacity: `${t * targetOpacity}`,
          };
        });
      }}
    />
  );
}

/** 路由切换时的过渡动画
 * 单位为百分比
 */
function RouterFly(
  props: NoKeyframeOptions<{
    a?: number;
    b?: number;
    opacity?: boolean;
  }>
) {
  const stack = useRouterStacks();
  const [, others] = splitProps(props, ['children']);
  return (
    <Transition
      {...props}
      active={false}
      config={(el, type) => {
        const a = props.a || 100;
        const b = props.b || -17;
        let x = (t: number) => t * a;
        const isIn = type === 'in';
        const isPop = stack.action === 'POP';
        let zIndex = 1;
        if (isIn) {
          if (isPop) {
            x = (t) => t * b;
          } else {
            zIndex = 2;
          }
        } else {
          if (!isPop) {
            x = (t) => t * b;
          } else {
            zIndex = 2;
          }
        }

        const opacity = props.opacity ? (t: number) => t : (_t: number) => 1;

        return makeOptions({ ...others, duration: props.duration || 350 }, (t) => {
          return {
            zIndex,
            opacity: String(opacity(t)),
            transform: `translateX(${x(1 - t)}%)`,
          };
        });
      }}
    />
  );
}

function Slide(props: NoKeyframeOptions<{ x?: boolean; y?: boolean }>) {
  return (
    <Transition
      {...props}
      config={(el) => {
        const rect = el.getBoundingClientRect();

        return makeOptions(props, (t) => {
          const height = props.x !== true ? { height: `${fix1px(rect.height * t)}px` } : undefined;
          const width = props.y !== true ? { width: `${fix1px(rect.width * t)}px` } : undefined;
          return {
            ...height,
            ...width,
            overflow: 'hidden',
          };
        });
      }}
    />
  );
}

const fromNode = new Map<string | number | symbol, Element>();
const toNode = new Map<string | number | symbol, Element>();
/** 交叉动画flip */
export function Cross(
  props: NoKeyframeOptions<{
    key: string;
    fallback?(t: number): Style;
  }>
) {
  const [, others] = splitProps(props, ['key', 'duration', 'fallback', 'children']);
  const active = useIsActive();
  const { key } = props;
  return (
    <Ref
      ref={(el: any) => {
        if (!el) return;
        createComputed(() => {
          if (active()) {
            toNode.set(key, el);
            microTasks(() => toNode.delete(key));
          }
        });
        transition(el, {
          ...others,
          async config(el, type) {
            let target: Element | undefined;
            if (type === 'out') {
              fromNode.set(key, el);
              target = await new Promise<Element>((res) => microTasks(() => res(toNode.get(key)!), true));
              fromNode.delete(key);
            } else {
              target = fromNode.get(key);
            }

            // console.log(type, target);

            return makeOptions(
              props,
              crossfade(el, () => target?.getBoundingClientRect(), props.fallback)
            );
          },
        });
      }}
    >
      {props.children}
    </Ref>
  );
}

function crossfade(
  node: Element,
  getFrom: () => DOMRect | undefined,
  fallback: (t: number) => Style = (t) => ({ opacity: t })
): (t: number) => Style {
  const to = node.getBoundingClientRect();
  const from = getFrom();
  if (!from) return fallback;
  const dx = from.left - to.left;
  const dy = from.top - to.top;
  const dw = from.width / to.width;
  const dh = from.height / to.height;
  return (t: number) => {
    const t2 = 1 - t;
    return {
      opacity: t,
      transformOrigin: 'left top',
      transform: `translate(${t2 * dx}px, ${t2 * dy}px) scale(${(1 - t) * dw + t}, ${(1 - t) * dh + t})`,
    };
  };
}

/** 指定一个位置(DOMRect)，从该位置飞到元素现在的位置 */
function From(
  props: NoKeyframeOptions<{
    rect: () => DOMRect | undefined;
    fallback?(t: number): Style;
  }>
) {
  const [, others] = splitProps(props, ['rect', 'duration', 'fallback']);
  return <Transition {...others} config={(el) => makeOptions(props, crossfade(el, props.rect, props.fallback))} />;
}

/** 生长动画,像一颗树一样 */
function Grow(props: NoKeyframeOptions) {
  const diff = 0.2;
  const [, others] = splitProps(props, ['children']);

  return (
    <Transition
      {...props}
      config={() => {
        return makeOptions({ ...others, easing: linear }, (t) => {
          const [x, y] = grow(t, diff);
          return {
            opacity: t < diff ? t / diff : 1,
            transform: `scale(${x}, ${y})`,
            transformOrigin: 'center bottom',
          };
        });
      }}
    />
  );
}

export type NoKeyframeOptions<T = {}> = ParentProps<Omit<Options, 'config'> & T>;

export default Object.assign(Transition, {
  Fade,
  Scale,
  Fly,
  Slide,
  Cross,
  From,
  Grow,
  RouterFly,
  Flip,
});
